import { useState, useEffect } from "react";
import { usePermissions } from "./PermissionsContext";
import { statusColor } from "./Utils/Utilities";
import { Badge } from "./SharedUI";

// ─── API setup ────────────────────────────────────────────────────────────────
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.seritonex.com";
const getToken = () => localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  accept: "application/json",
  ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
});

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    console.error(`apiFetch ${method} ${path}:`, e);
    throw e;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function resolveUserId(userOrId) {
  if (userOrId == null) return null;
  if (typeof userOrId === "number" && !isNaN(userOrId)) return userOrId;
  if (typeof userOrId === "string") {
    const n = Number(userOrId);
    if (!isNaN(n) && n > 0) return n;
  }
  if (typeof userOrId === "object") {
    const raw = userOrId._rawUserId ?? userOrId.user_id ?? userOrId.id;
    if (raw != null && !isNaN(Number(raw)) && Number(raw) > 0)
      return Number(raw);
  }
  return null;
}

const decodeToken = () => {
  try {
    const jwt = getToken();
    if (!jwt) return null;
    const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const payload = decodeToken();
  if (!payload) return null;
  return resolveUserId(payload.user_id ?? payload.id ?? payload.sub ?? null);
};

const fmtDate = (raw) => {
  if (
    !raw ||
    String(raw).startsWith("undefined") ||
    String(raw).startsWith("0000")
  )
    return "—";
  try {
    const d = new Date(raw.replace(" ", "T"));
    if (isNaN(d.getTime())) return "—";
    return (
      d.toLocaleDateString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      ", " +
      d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return raw;
  }
};

// ─── API calls ────────────────────────────────────────────────────────────────
const getBranchesByUser = (user_id) =>
  apiFetch("POST", "/api/v1/amis/branch/by-user-id", {
    user_id: resolveUserId(user_id),
  });

const getTransitsByBranch = (branch_id) =>
  apiFetch("POST", "/api/v1/amis/transit/open-by-branch", {
    branch_id: parseInt(branch_id, 10),
  });

// ─── Normalise transit ────────────────────────────────────────────────────────
const normalizeTransit = (t) => {
  const raw_completion = t.completion_date ?? "";
  const completionDate =
    raw_completion.startsWith("undefined") || raw_completion.startsWith("0000")
      ? null
      : raw_completion;

  const allAssets = Array.isArray(t.assets) ? t.assets : [];
  const validAssets = allAssets.filter(
    (a) => a.asset_id != null && a.asset_id !== 0,
  );
  const damagedCount = allAssets.filter(
    (a) => a.asset_condition?.description === "Damaged",
  ).length;

  return {
    id: t.transit_id,
    transitNumber: t.transit_number ?? "-",
    refDocId: t.reference_doc_id ?? "-",
    driverId: t.driver,
    vehicle: t.vehicle_registration_number ?? "—",
    dispatchDate: t.dispatch_date ?? null,
    completionDate,
    notes: t.notes ?? "",
    type: t.transit_type?.description ?? "-",
    status: t.transit_status?.description ?? "Unknown",
    assetCount: validAssets.length,
    damagedCount,
    assets: allAssets,
    _raw: t,
  };
};

// ─── Status config ────────────────────────────────────────────────────────────
const STAT_KEYS = ["Dispatched", "Completed"];

// ─── Badge helpers ────────────────────────────────────────────────────────────
const conditionStyle = (desc) => {
  if (desc === "Damaged")
    return {
      color: "var(--danger)",
      bg: "rgba(255,82,82,.1)",
      border: "rgba(255,82,82,.25)",
    };
  return {
    color: "var(--teal)",
    bg: "rgba(29,233,182,.1)",
    border: "rgba(29,233,182,.25)",
  };
};

const transitStatusStyle = (desc) => {
  if (desc === "In Transit")
    return {
      color: "var(--blue)",
      bg: "rgba(64,196,255,.1)",
      border: "rgba(64,196,255,.25)",
    };
  if (desc === "Delivered")
    return {
      color: "var(--teal)",
      bg: "rgba(29,233,182,.1)",
      border: "rgba(29,233,182,.25)",
    };
  return {
    color: "var(--tc3)",
    bg: "var(--surface-2)",
    border: "var(--border)",
  };
};

function Pill({ label, style: s }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 20,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── Assets page ──────────────────────────────────────────────────────────────
function AssetsPage({ transit, onBack }) {
  const [search, setSearch] = useState("");
  const [filterCondition, setFilterCondition] = useState("All");

  const assets = transit.assets ?? [];

  const typeSummary = assets.reduce((acc, a) => {
    const k = a.asset_type_description ?? "Unknown";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const damagedCount = assets.filter(
    (a) => a.asset_condition?.description === "Damaged",
  ).length;

  const filtered = assets.filter((a) => {
    const matchCondition =
      filterCondition === "All" ||
      a.asset_condition?.description === filterCondition;
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      (a.asset_serial ?? "").toLowerCase().includes(q) ||
      (a.asset_type_description ?? "").toLowerCase().includes(q) ||
      (a.asset_type_code ?? "").toLowerCase().includes(q) ||
      (a.from_warehouse?.warehouse_name ?? "").toLowerCase().includes(q) ||
      (a.to_warehouse?.warehouse_name ?? "").toLowerCase().includes(q);
    return matchCondition && matchSearch;
  });

  return (
    <div className="page">
      <div className="panel mb-16">
        <div
          className="ph"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <i
            className="bi bi-box-seam"
            style={{ fontSize: 14, color: "var(--flame)" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="ph-title">
              Assets
              <span
                className="tc3 fs11"
                style={{ marginLeft: 8, fontWeight: 400 }}
              >
                — {transit.transitNumber}
              </span>
            </span>
            <div className="mono fs10 tc3" style={{ marginTop: 1 }}>
              {transit.vehicle}
              {transit.dispatchDate
                ? ` · ${fmtDate(transit.dispatchDate)}`
                : ""}
              {transit.notes ? ` · ${transit.notes}` : ""}
            </div>
          </div>
          <Badge status={transit.status} />
        </div>
      </div>

      <div className="fc gap8 mb16" style={{ flexWrap: "wrap" }}>
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 16px",
          }}
        >
          <div className="syne fw7 fs18" style={{ color: "var(--flame)" }}>
            {assets.length}
          </div>
          <div className="fs10 tc3">Total Assets</div>
        </div>

        {damagedCount > 0 && (
          <div
            style={{
              background: "rgba(255,82,82,.06)",
              border: "1px solid rgba(255,82,82,.25)",
              borderRadius: 8,
              padding: "10px 16px",
            }}
          >
            <div className="syne fw7 fs18" style={{ color: "var(--danger)" }}>
              {damagedCount}
            </div>
            <div className="fs10" style={{ color: "var(--danger)" }}>
              Damaged
            </div>
          </div>
        )}

        {Object.entries(typeSummary).map(([type, count]) => (
          <div
            key={type}
            style={{
              background: "rgba(29,233,182,.06)",
              border: "1px solid rgba(29,233,182,.2)",
              borderRadius: 8,
              padding: "10px 16px",
            }}
          >
            <div className="syne fw7 fs18" style={{ color: "var(--teal)" }}>
              {count}
            </div>
            <div className="fs10 tc2">{type}</div>
          </div>
        ))}
      </div>

      <div className="panel">
        <div className="tbar">
          <span className="ph-title">Asset List</span>
          <div className="tsearch" style={{ maxWidth: 240 }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search serial, type, warehouse…"
            />
          </div>
          <select
            className="tfilter"
            value={filterCondition}
            onChange={(e) => setFilterCondition(e.target.value)}
          >
            {["All", "Good", "Damaged"].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <span className="fs11 tc3 mono">{filtered.length} records</span>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Serial</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Condition</th>
                <th>Status</th>
                <th>Scanned</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => {
                const cond = a.asset_condition?.description ?? "—";
                const cSty = conditionStyle(cond);
                const tstat = a.transit_status?.description ?? "—";
                const tSty = transitStatusStyle(tstat);
                return (
                  <tr key={a.asset_transit_id ?? a.asset_id ?? i}>
                    <td className="mono tc3 fs10">{i + 1}</td>
                    <td>
                      <span className="mono fw-6 fs11 t-flame">
                        {a.asset_serial ?? "—"}
                      </span>
                      <div className="mono tc3 fs10">ID #{a.asset_id}</div>
                    </td>
                    <td>
                      <div className="fs11 tc1 fw-6">
                        {a.asset_type_description ?? "—"}
                      </div>
                      <div className="mono tc3 fs10">
                        {a.asset_type_code ?? ""}
                      </div>
                    </td>
                    <td>
                      <div className="fs11 tc2">
                        {a.from_warehouse?.warehouse_name ?? "—"}
                      </div>
                      <div className="tc3 fs10">
                        {a.from_warehouse?.branch?.branch_name ?? ""}
                      </div>
                    </td>
                    <td>
                      <div className="fs11 tc2">
                        {a.to_warehouse?.warehouse_name ?? "—"}
                      </div>
                      <div className="tc3 fs10">
                        {a.to_warehouse?.branch?.branch_name ?? ""}
                      </div>
                    </td>
                    <td>
                      <Pill label={cond} style={cSty} />
                    </td>
                    <td>
                      <Pill label={tstat} style={tSty} />
                    </td>
                    <td className="mono tc3 fs10">
                      {fmtDate(a.scan_timestamp)}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="empty">
                      <div className="empty-ico">📦</div>Not Found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop: 18 }}>
        <button className="btn btn-g btn-sm" onClick={onBack}>
          Back to Transits
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
function DeliveriesPage({ showToast: toast }) {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchLoading, setBranchLoading] = useState(true);

  const [transits, setTransits] = useState([]);
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [assetTransit, setAssetTransit] = useState(null);

  // ── 1. Load branches ──────────────────────────────────────────────────────
  useEffect(() => {
    const uid = getCurrentUserId();
    if (!uid) {
      toast("Could not read user ID from token", "e");
      setBranchLoading(false);
      return;
    }
    setBranchLoading(true);
    getBranchesByUser(uid)
      .then((res) => {
        let arr;
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === "object") {
          const b = res.branches;
          if (Array.isArray(b)) arr = b;
          else if (b && typeof b === "object") arr = [b];
          else if (Array.isArray(res.data)) arr = res.data;
          else if (res.branch_id != null || res.id != null) arr = [res];
          else arr = [];
        } else {
          arr = [];
        }
        setBranches(arr);
        if (arr.length > 0) setSelectedBranch(arr[0].branch_id ?? arr[0].id);
      })
      .catch((err) => {
        setBranches([]);
        toast(err.message, "e");
      })
      .finally(() => setBranchLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 2. Load transits when branch changes ──────────────────────────────────
  useEffect(() => {
    if (!selectedBranch) return;
    loadTransits(selectedBranch);
  }, [selectedBranch]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadTransits(branchId) {
    setLoading(true);
    setError(null);
    getTransitsByBranch(branchId)
      .then((res) => {
        const arr = Array.isArray(res)
          ? res
          : (res?.transits ?? res?.data ?? []);
        setTransits(arr.map(normalizeTransit));
      })
      .catch((err) => {
        setError(err.message);
        toast(err.message, "e");
      })
      .finally(() => setLoading(false));
  }

  const { canSeePage, ready } = usePermissions();

  if (ready && !canSeePage("deliveries")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view deliveries.</p>
      </div>
    );
  }

  if (assetTransit) {
    return (
      <AssetsPage transit={assetTransit} onBack={() => setAssetTransit(null)} />
    );
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const safeBranches = Array.isArray(branches) ? branches : [];
  const onlyOneBranch = safeBranches.length <= 1;

  const selectedBranchName =
    safeBranches.find((b) => (b.branch_id ?? b.id) === selectedBranch)
      ?.branch_name ?? "";

  const filtered = transits.filter((t) => {
    const matchStatus = filterStatus === "All" || t.status === filterStatus;
    const matchSearch =
      !search ||
      t.transitNumber.toLowerCase().includes(search.toLowerCase()) ||
      t.vehicle.toLowerCase().includes(search.toLowerCase()) ||
      t.type.toLowerCase().includes(search.toLowerCase()) ||
      String(t.refDocId).includes(search) ||
      t.notes.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const stats = STAT_KEYS.map((s) => ({
    s,
    n: transits.filter((t) => t.status === s).length,
  }));

  return (
    <div className="page">
      {/* Stat cards */}
      <div className="fc gap8 mb16" style={{ flexWrap: "wrap" }}>
        {stats.map(({ s, n }) => (
          <div
            key={s}
            style={{
              background: "var(--surface)",
              border: `1px solid ${statusColor(s)}30`,
              borderRadius: 8,
              padding: "10px 16px",
              cursor: "pointer",
              outline:
                filterStatus === s ? `2px solid ${statusColor(s)}` : "none",
              transition: "all .13s",
            }}
            onClick={() => setFilterStatus((prev) => (prev === s ? "All" : s))}
          >
            <div className="syne fw7 fs18" style={{ color: statusColor(s) }}>
              {loading ? "…" : n}
            </div>
            <div className="fs10 tc3">{s}</div>
          </div>
        ))}
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 16px",
            cursor: "pointer",
            outline: filterStatus === "All" ? "2px solid var(--flame)" : "none",
            transition: "all .13s",
          }}
          onClick={() => setFilterStatus("All")}
        >
          <div className="syne fw7 fs18" style={{ color: "var(--flame)" }}>
            {loading ? "…" : transits.length}
          </div>
          <div className="fs10 tc3">All</div>
        </div>
      </div>

      {/* Main table panel */}
      <div className="panel">
        <div className="tbar">
          {/* Title + branch dropdown inline */}
          <span
            className="ph-title"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            Transits
            {branchLoading ? (
              <span className="tc3 fs11" style={{ fontWeight: 400 }}>
                —
              </span>
            ) : (
              <select
                className="tfilter"
                style={{ fontWeight: 600, fontSize: 12 }}
                value={selectedBranch ?? ""}
                disabled={onlyOneBranch}
                onChange={(e) => {
                  setSelectedBranch(Number(e.target.value));
                  setFilterStatus("All");
                  setSearch("");
                }}
              >
                {safeBranches.map((b) => {
                  const bid = b.branch_id ?? b.id;
                  const name = b.branch_name ?? b.name ?? `Branch ${bid}`;
                  return (
                    <option key={bid} value={bid}>
                      {name}
                    </option>
                  );
                })}
              </select>
            )}
          </span>

          {/* Search */}
          <div className="tsearch" style={{ maxWidth: 220 }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transits…"
            />
          </div>

          {/* Status filter */}
          <select
            className="tfilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {["All", "Dispatched", "Completed", "Pending"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          {/* Refresh */}
          <button
            className="btn btn-g btn-sm btn-ico"
            title="Refresh"
            disabled={loading || !selectedBranch}
            onClick={() => selectedBranch && loadTransits(selectedBranch)}
          >
            <i
              className={`bi bi-arrow-clockwise${loading ? " spin" : ""}`}
              style={{ fontSize: 13 }}
            />
          </button>

          <span className="fs11 tc3 mono">{filtered.length} records</span>
        </div>

        {loading && (
          <div className="empty">
            <div className="empty-ico"></div>Loading transits…
          </div>
        )}
        {!loading && !selectedBranch && !error && (
          <div className="empty">
            <div className="empty-ico">🏢</div>Select a branch above
          </div>
        )}
        {!loading && error && (
          <div className="empty">
            <div className="empty-ico">⚠️</div>
            {error}
          </div>
        )}

        {!loading && !error && selectedBranch && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Transit #</th>
                  <th>Ref Doc</th>
                  <th>Vehicle</th>
                  <th>Type</th>
                  <th>Dispatched</th>
                  <th>Assets</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <span className="mono t-flame fw-6">
                        {t.transitNumber}
                      </span>
                      <div className="tc3 fs10 mono">#{t.id}</div>
                    </td>
                    <td className="mono tc2 fs11">
                      {t.refDocId !== 0 && t.refDocId !== "0"
                        ? t.refDocId
                        : "—"}
                    </td>
                    <td className="mono fw-6 fs11">{t.vehicle}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 20,
                          background:
                            t.type === "Return from Customer"
                              ? "rgba(64,196,255,.1)"
                              : "rgba(29,233,182,.1)",
                          color:
                            t.type === "Return from Customer"
                              ? "var(--blue)"
                              : "var(--teal)",
                          border:
                            t.type === "Return from Customer"
                              ? "1px solid rgba(64,196,255,.25)"
                              : "1px solid rgba(29,233,182,.25)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.type === "Return from Customer"
                          ? "↩ Return"
                          : "↗ Issue"}
                      </span>
                    </td>
                    <td className="mono fs10 tc2">{fmtDate(t.dispatchDate)}</td>
                    <td>
                      <span className="fw-6 fs12">{t.assetCount}</span>
                      {t.damagedCount > 0 && (
                        <span
                          style={{
                            marginLeft: 5,
                            fontSize: 10,
                            color: "var(--danger)",
                            fontWeight: 700,
                          }}
                        >
                          {t.damagedCount} dmg
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge status={t.status} />
                    </td>
                    <td
                      className="tc3 fs11"
                      style={{
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={t.notes}
                    >
                      {t.notes || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="btn btn-g btn-sm"
                        title={`View ${t.assetCount} asset${t.assetCount !== 1 ? "s" : ""}`}
                        onClick={() => setAssetTransit(t)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 5,
                        }}
                      >
                        <i
                          className="bi bi-box-seam"
                          style={{ fontSize: 12 }}
                        />
                        <span className="fs11">{t.assetCount}</span>
                      </button>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10}>
                      <div className="empty">
                        <div className="empty-ico"></div>No transits found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeliveriesPage;
